import { NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarClock, FileText, ListChecks, ReceiptText } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { haptics } from "@/lib/haptics";

/**
 * Bottom tab bar for the trip detail screen. Replaces the sticky
 * top-of-page tab strip so horizontal swipes never collide with row
 * gestures, and the primary destinations sit where the thumb reaches.
 *
 * The tab-specific "Add" CTA now floats on the hero (see the hero
 * FAB in trip.$id.tsx), so this bar only carries navigation.
 * Safe-area aware for iOS PWA + Capacitor Android edge-to-edge.
 */

export type TripActiveTab = "timeline" | "plan" | "expenses" | "documents";

interface TripActionBarProps {
  tripId: string;
  activeTab: TripActiveTab;
  /** Kept for API stability with earlier callers — unused for the tab bar. */
  readOnly?: boolean;
}

const TABS: Array<{ key: TripActiveTab; label: string; icon: LucideIcon }> = [
  { key: "timeline", label: "Timeline", icon: CalendarClock },
  { key: "plan", label: "Plan", icon: ListChecks },
  { key: "expenses", label: "Expenses", icon: ReceiptText },
  { key: "documents", label: "Docs", icon: FileText },
];

export function TripActionBar({ tripId, activeTab }: TripActionBarProps) {
  return (
    <nav
      aria-label="Trip sections"
      data-ad-clip="bottom"
      data-tour="trip-tabs"
      className={cn(
        "fixed inset-x-0 bottom-0 z-30",
        "border-t border-line bg-[color-mix(in_oklab,var(--bg-elevated)_92%,transparent)]",
        "backdrop-blur-md",
      )}
      style={{ paddingBottom: "var(--safe-bottom)" }}
    >
      <ul className="mx-auto flex max-w-md items-stretch justify-around px-2 pt-1.5 pb-2">
        {TABS.map((tab) => {
          const isActive = tab.key === activeTab;
          return (
            <li key={tab.key} className="flex-1">
              <NavLink
                to={`/trip/${tripId}/${tab.key}`}
                onPointerDown={() => haptics.tap()}
                id={`trip-tab-${tab.key}`}
                role="tab"
                aria-selected={isActive}
                className={cn(
                  "relative flex flex-col items-center gap-1 py-1.5 text-[11px] font-semibold transition-colors",
                  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary rounded-md",
                  isActive ? "text-primary" : "text-ink-subtle hover:text-ink",
                )}
              >
                <tab.icon className="h-5 w-5" strokeWidth={isActive ? 2.25 : 1.75} />
                <span>{tab.label}</span>
                {isActive ? (
                  <motion.span
                    layoutId="trip-tab-active"
                    aria-hidden
                    className="absolute -top-1 left-1/2 h-1 w-8 -translate-x-1/2 rounded-full bg-primary"
                    transition={{ type: "spring", stiffness: 500, damping: 40 }}
                  />
                ) : null}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * Vertical space the tab bar reserves — apply as bottom padding on the
 * scrolling content so nothing hides behind it. Includes safe-area.
 * (Icon + label ≈ 56 px + 8 px breathing room.)
 */
export const TRIP_ACTION_BAR_SPACER =
  "pb-[calc(64px+var(--safe-bottom))]";
